import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PageShell,
  RadioGroup,
  SectionCard,
  Select,
  toast,
} from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type {
  ArtistCreateRequest,
  ArtistGroupCreateRequest,
  ArtistAlias,
  ArtistAliasLanguageEnum,
  ArtistImage,
  ArtistImageTypeEnum,
} from '@/lib/api-admin/types';
import {
  ARTIST_GENDERS,
  ARTIST_TYPES,
  artistCreateSchema,
  emptyArtistCreateValues,
  type ArtistCreateValues,
} from '../schemas/artistFormSchema';
import {
  useCreateArtistGroupMutation,
  useCreateArtistMutation,
} from '../hooks/useArtistMutation';
import { useUploadArtistImageMutation } from '../hooks/useArtistImageMutation';
import { ArtistImageUploader } from '../components/ArtistImageUploader';
import { ArtistAliasManager } from '../components/ArtistAliasManager';

/** 등록 전에 고른 이미지 — 업로드 API 가 artistId 를 요구해 등록 직후 올린다. */
interface StagedImage {
  image: ArtistImage;
  file: File;
  altText?: string;
}

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

const stripEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== '' && v !== undefined && v !== null) (out as Record<string, unknown>)[k] = v;
  }
  return out;
};

const toCreatePayload = (values: ArtistCreateValues): ArtistCreateRequest => {
  const cleaned = stripEmpty(values);
  return {
    type: values.type,
    stageName: values.stageName,
    ...cleaned,
  } as ArtistCreateRequest;
};

// 그룹 등록 요청은 ArtistGroupCreateRequest 필드만 보낸다. `type` 과 솔로 전용
// 필드(legalName/gender/birthDate/nationality)는 그룹 스키마에 없으므로 stripEmpty 결과를
// 통째로 스프레드하지 않고 명시 화이트리스트로 추린다(불필요한 필드 누출 방지).
const toGroupPayload = (values: ArtistCreateValues): ArtistGroupCreateRequest => {
  const groupFields = {
    debutDate: values.debutDate,
    fanClubName: values.fanClubName,
    agencyName: values.agencyName,
    bio: values.bio,
    ...(values.initialAliases && values.initialAliases.length > 0
      ? { initialAliases: values.initialAliases }
      : {}),
  };
  const cleaned = stripEmpty(groupFields);
  return {
    stageName: values.stageName,
    ...cleaned,
  } as ArtistGroupCreateRequest;
};

export const ArtistCreatePage = () => {
  const { t } = useTranslation('artists');
  const navigate = useNavigate();
  const soloCreate = useCreateArtistMutation();
  const groupCreate = useCreateArtistGroupMutation();

  const form = useForm<ArtistCreateValues>({
    resolver: zodResolver(artistCreateSchema),
    defaultValues: emptyArtistCreateValues(),
    mode: 'onBlur',
  });
  const type = form.watch('type');
  const isGroup = type === 'GROUP';
  const mutation = isGroup ? groupCreate : soloCreate;
  const error = errorMessageOf(mutation.error);

  // 등록 화면에서도 이미지를 고른다(종전엔 상세에서만 가능). 미리보기만 두고, 등록이 끝나면 차례로 올린다.
  const uploadImage = useUploadArtistImageMutation();
  const [staged, setStaged] = useState<StagedImage[]>([]);
  const [uploadingStaged, setUploadingStaged] = useState(false);
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  useEffect(() => () => stagedRef.current.forEach((x) => URL.revokeObjectURL(x.image.url)), []);

  const stageImage = ({ file, type: imageType, altText }: { file: File; type: ArtistImageTypeEnum; altText?: string }) => {
    const url = URL.createObjectURL(file);
    setStaged((prev) => [
      ...prev,
      {
        file,
        ...(altText ? { altText } : {}),
        image: {
          imageId: `staged-${Date.now()}-${prev.length}`,
          type: imageType,
          url,
          sortOrder: prev.filter((x) => x.image.type === imageType).length,
          altText: altText ?? null,
          fileSize: file.size,
          contentType: file.type,
          uploadedAt: new Date().toISOString(),
        },
      },
    ]);
  };
  const unstageImage = (imageId: string) =>
    setStaged((prev) => {
      const hit = prev.find((x) => x.image.imageId === imageId);
      if (hit) URL.revokeObjectURL(hit.image.url);
      return prev.filter((x) => x.image.imageId !== imageId);
    });

  const afterCreate = async (artistId: string) => {
    if (staged.length > 0) {
      setUploadingStaged(true);
      let failed = 0;
      for (const x of staged) {
        try {
          await uploadImage.mutateAsync({
            artistId,
            file: x.file,
            type: x.image.type,
            ...(x.altText ? { altText: x.altText } : {}),
          });
        } catch {
          failed += 1;
        }
      }
      setUploadingStaged(false);
      if (failed > 0) {
        toast.error(
          t('artists.create.imagesPartialFail', {
            failed,
            defaultValue: `아티스트는 등록됐지만 이미지 ${failed}장을 올리지 못했습니다. 상세 화면에서 다시 올려 주세요.`,
          }),
        );
      }
    }
    // QA-418 — 등록이 끝나면 목록으로(등록 화면과 수정 화면이 같은 양식이라 상세로 가면 아직 등록 중인 것으로 오인했다).
    toast.success(t('artists.create.created', { defaultValue: '아티스트를 등록했습니다.' }));
    navigate('/artists');
  };

  // 다국어 별칭 — 수정 화면과 같은 입력(언어·별칭·대표 여부). 등록 요청의 initialAliases(최대 20개)로 함께 보낸다.
  const [aliases, setAliases] = useState<ArtistAlias[]>([]);
  const [aliasError, setAliasError] = useState<string | null>(null);
  const stageAlias = ({ language, aliasValue, isPrimary }: { language: ArtistAliasLanguageEnum; aliasValue: string; isPrimary: boolean }) => {
    setAliasError(null);
    const value = aliasValue.trim();
    if (aliases.some((a) => a.language === language && a.aliasValue === value)) {
      setAliasError(t('artists.errors.aliasDuplicate', { defaultValue: '같은 언어에 같은 별칭이 이미 있습니다.' }));
      return;
    }
    if (aliases.length >= 20) {
      setAliasError(t('artists.errors.aliasTooMany', { defaultValue: '별칭은 20개까지 등록할 수 있습니다.' }));
      return;
    }
    setAliases((prev) => [
      // 대표 별칭은 언어마다 하나(BE 가드) — 새로 대표로 고르면 같은 언어의 기존 대표를 내린다.
      ...prev.map((a) => (isPrimary && a.language === language ? { ...a, isPrimary: false } : a)),
      { aliasId: `staged-${Date.now()}-${prev.length}`, language, aliasValue: value, isPrimary },
    ]);
  };
  const unstageAlias = (aliasId: string) => setAliases((prev) => prev.filter((a) => a.aliasId !== aliasId));

  const onSubmit = (formValues: ArtistCreateValues) => {
    const values: ArtistCreateValues = {
      ...formValues,
      initialAliases: aliases.map(({ language, aliasValue, isPrimary }) => ({ language, aliasValue, isPrimary: !!isPrimary })),
    };
    if (values.type === 'GROUP') {
      groupCreate.mutate(toGroupPayload(values), {
        onSuccess: (artist) => void afterCreate(artist.artistId),
      });
    } else {
      soloCreate.mutate(toCreatePayload(values), {
        onSuccess: (artist) => void afterCreate(artist.artistId),
      });
    }
  };
  const busy = mutation.isPending || uploadingStaged;

  return (
    <PageShell
      title={t('artists.create.title')}
      breadcrumb={[
        { label: t('artists.list.title'), href: '/artists' },
        { label: t('artists.create.breadcrumbCurrent') },
      ]}
      actions={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/artists')}
            disabled={busy}
          >
            {t('artists.actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="cta"
            disabled={busy}
            onClick={form.handleSubmit(onSubmit)}
            data-testid="artist-create-submit"
          >
            {uploadingStaged
              ? t('artists.create.uploadingImages', { defaultValue: '이미지 업로드 중…' })
              : mutation.isPending
                ? t('artists.create.submitting')
                : t('artists.actions.create')}
          </Button>
        </>
      }
    >

      {error ? (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {error}
        </p>
      ) : null}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          aria-label={t('artists.create.formLabel')}
          noValidate
          data-testid="artist-create-form"
          className="grid grid-cols-1 gap-4 lg:grid-cols-2"
        >
          {/* 수정 화면(ArtistDetailPage)과 같은 배치 — [기본 정보 | 다국어 별칭] / [이미지(전체 폭)]. 유형만 등록 전용. */}
            <SectionCard title={t('artists.detail.sectionInfo')}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t('artists.fields.type')}
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          orientation="horizontal"
                          aria-label={t('artists.fields.type')}
                          data-testid="artist-type-select"
                          options={ARTIST_TYPES.map((opt) => ({
                            value: opt,
                            label: t(`artists.type.${opt}`),
                          }))}
                          className="min-h-[var(--density-compact-form-h)] items-center"
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.type?.message ? t(form.formState.errors.type.message, { defaultValue: form.formState.errors.type.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="stageName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {t('artists.fields.stageName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          required
                          aria-required="true"
                          maxLength={100}
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.stageName?.message ? t(form.formState.errors.stageName.message, { defaultValue: form.formState.errors.stageName.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                {!isGroup ? (
                  <FormField
                    control={form.control}
                    name="legalName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('artists.fields.legalName')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            maxLength={100}
                            style={{ height: 'var(--density-compact-form-h)' }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ) : null}

                {!isGroup ? (
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('artists.fields.gender')}</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            style={{ height: 'var(--density-compact-form-h)' }}
                          >
                            <option value="">—</option>
                            {ARTIST_GENDERS.map((opt) => (
                              <option key={opt} value={opt}>{t(`artists.gender.${opt}`)}</option>
                            ))}
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                ) : null}

                <FormField
                  control={form.control}
                  name="nationality"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('artists.fields.nationality')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          maxLength={80}
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="debutDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('artists.fields.debutDate')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          type="date"
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.debutDate?.message ? t(form.formState.errors.debutDate.message, { defaultValue: form.formState.errors.debutDate.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />

                {!isGroup ? (
                  <FormField
                    control={form.control}
                    name="birthDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('artists.fields.birthDate')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            type="date"
                            style={{ height: 'var(--density-compact-form-h)' }}
                          />
                        </FormControl>
                        <FormMessage>{form.formState.errors.birthDate?.message ? t(form.formState.errors.birthDate.message, { defaultValue: form.formState.errors.birthDate.message }) : null}</FormMessage>
                      </FormItem>
                    )}
                  />
                ) : null}

                <FormField
                  control={form.control}
                  name="fanClubName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('artists.fields.fanClubName')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          maxLength={80}
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="agencyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('artists.fields.agencyName')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ''}
                          maxLength={100}
                          style={{ height: 'var(--density-compact-form-h)' }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.agencyName?.message ? t(form.formState.errors.agencyName.message, { defaultValue: form.formState.errors.agencyName.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem className="col-span-full">
                      <FormLabel>{t('artists.fields.bio')}</FormLabel>
                      <FormControl>
                        <textarea
                          id="bio"
                          {...field}
                          value={field.value ?? ''}
                          maxLength={4000}
                          rows={4}
                          className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
                          style={{
                            background: 'var(--surface-0)',
                            color: 'var(--content-strong)',
                            borderColor: 'var(--content-muted)',
                          }}
                        />
                      </FormControl>
                      <FormMessage>{form.formState.errors.bio?.message ? t(form.formState.errors.bio.message, { defaultValue: form.formState.errors.bio.message }) : null}</FormMessage>
                    </FormItem>
                  )}
                />
              </div>
            </SectionCard>

            <SectionCard title={t('artists.detail.sectionAliases')}>
              <ArtistAliasManager
                aliases={aliases}
                isAdding={false}
                isDeleting={busy}
                errorMessage={aliasError}
                onAdd={stageAlias}
                onDelete={unstageAlias}
              />
            </SectionCard>

          <div className="lg:col-span-2">
            <SectionCard
              title={t('artists.detail.sectionImages')}
              description={t('artists.create.imagesHint', { defaultValue: '[등록]하면 아티스트와 함께 업로드됩니다.' })}
            >
              <ArtistImageUploader
                images={staged.map((x) => x.image)}
                isUploading={uploadingStaged}
                isDeleting={busy}
                onUpload={stageImage}
                onDelete={unstageImage}
              />
            </SectionCard>
          </div>
        </form>
      </Form>
    </PageShell>
  );
};
